import { parse } from "yaml";
import { z } from "zod";
import { extractSourceReferenceNumbers } from "@/lib/MarkdownInline";

export interface ArticleSource {
  title: string;
  url: string;
  note?: string;
}

// categories/tagsは将来のURL設計(/articles/categories/…, /articles/tags/…)のために予約している。
export const RESERVED_ARTICLE_SLUGS = [
  "categories",
  "tags",
  "category",
  "tag",
] as const;

const ARTICLE_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const ARTICLE_CATEGORIES = ["暮らし", "ごはん"] as const;
export type ArticleCategory = (typeof ARTICLE_CATEGORIES)[number];

export const ARTICLE_TAGS = [
  "費用",
  "猫を迎える",
  "カロリー",
  "おやつ",
] as const;
export type ArticleTag = (typeof ARTICLE_TAGS)[number];

function isArticleCategory(value: string): value is ArticleCategory {
  return (ARTICLE_CATEGORIES as readonly string[]).includes(value);
}

function isArticleTag(value: string): value is ArticleTag {
  return (ARTICLE_TAGS as readonly string[]).includes(value);
}

export interface Article {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt: string;
  category: ArticleCategory;
  tags: readonly ArticleTag[];
  published: boolean;
  body: string;
  sources: readonly ArticleSource[];
  ogImageUrl?: string;
  /**
   * 広告枠の既定カテゴリを、この記事だけ上書きする広告カテゴリ。省略すると枠の既定カテゴリを使う
   */
  adCategories?: readonly string[];
}

/**
 * 記事用OGP画像のファイル名から、ビルド後に配信されるURLへの対応
 */
export type ArticleOgImageUrls = Readonly<Record<string, string>>;

export type ArticleSummary = Omit<Article, "body" | "sources" | "adCategories">;

export class ArticleParseError extends Error {
  constructor(slug: string, reason: string) {
    super(`記事(${slug})のfrontmatterを解析できませんでした: ${reason}`);
    this.name = "ArticleParseError";
  }
}

const ArticleFrontmatterSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  publishedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  updatedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  category: z
    .string()
    .min(1)
    .superRefine((value, ctx) => {
      if (!isArticleCategory(value)) {
        ctx.addIssue({
          code: "custom",
          message: `不明なcategoryです: ${value}`,
        });
      }
    }),
  tags: z
    .array(z.string().min(1))
    .default([])
    .superRefine((tags, ctx) => {
      for (const tag of tags) {
        if (!isArticleTag(tag)) {
          ctx.addIssue({ code: "custom", message: `不明なtagです: ${tag}` });
        }
      }
      if (new Set(tags).size !== tags.length) {
        ctx.addIssue({ code: "custom", message: "tagsに重複があります" });
      }
    }),
  published: z.boolean().default(false),
  sources: z
    .array(
      z.object({
        title: z.string().min(1),
        url: z.string().url(),
        note: z.string().min(1).optional(),
      }),
    )
    .default([]),
  ogImage: z.string().min(1).optional(),
  adCategories: z
    .array(z.string().min(1))
    .optional()
    .superRefine((categories, ctx) => {
      if (categories && new Set(categories).size !== categories.length) {
        ctx.addIssue({
          code: "custom",
          message: "adCategoriesに重複があります",
        });
      }
    }),
});

const FRONTMATTER_DELIMITER = "---";

export function articleSlugFromFilePath(filePath: string): string {
  const fileName = filePath.split("/").pop() ?? filePath;
  return fileName.replace(/\.md$/, "");
}

export function validateArticleSlug(slug: string): void {
  if ((RESERVED_ARTICLE_SLUGS as readonly string[]).includes(slug)) {
    throw new ArticleParseError(
      slug,
      `slug「${slug}」は将来のURL設計のために予約されています`,
    );
  }
  if (!ARTICLE_SLUG_PATTERN.test(slug)) {
    throw new ArticleParseError(
      slug,
      `slugの形式が不正です(小文字英数字とハイフンのみ、例: cat-cost)`,
    );
  }
}

export function parseArticle(
  slug: string,
  source: string,
  ogImageUrls: ArticleOgImageUrls = {},
  adCategoryIds: readonly string[] = [],
): Article {
  validateArticleSlug(slug);

  const frontmatterSource = extractFrontmatterSource(source);
  if (frontmatterSource === null) {
    throw new ArticleParseError(slug, "frontmatter not found");
  }

  const parsedYaml: unknown = parse(frontmatterSource.frontmatter);
  const result = ArticleFrontmatterSchema.safeParse(parsedYaml);
  if (!result.success) {
    throw new ArticleParseError(slug, z.prettifyError(result.error));
  }

  const frontmatter = result.data;
  const body = frontmatterSource.body.replace(/^(\s*\n)+/, "").trimEnd();

  validateSourceReferences(slug, body, frontmatter.sources.length);
  validateAdCategories(slug, frontmatter.adCategories, adCategoryIds);

  return {
    slug,
    title: frontmatter.title,
    description: frontmatter.description,
    publishedAt: frontmatter.publishedAt,
    updatedAt: frontmatter.updatedAt ?? frontmatter.publishedAt,
    category: frontmatter.category as ArticleCategory,
    tags: frontmatter.tags as ArticleTag[],
    published: frontmatter.published,
    body,
    sources: frontmatter.sources,
    ogImageUrl: resolveOgImageUrl(slug, frontmatter.ogImage, ogImageUrls),
    adCategories: frontmatter.adCategories,
  };
}

function validateAdCategories(
  slug: string,
  adCategories: readonly string[] | undefined,
  adCategoryIds: readonly string[],
): void {
  for (const category of adCategories ?? []) {
    if (!adCategoryIds.includes(category)) {
      throw new ArticleParseError(
        slug,
        `adCategoriesの「${category}」が広告の設定のcategoriesにありません`,
      );
    }
  }
}

function resolveOgImageUrl(
  slug: string,
  ogImage: string | undefined,
  ogImageUrls: ArticleOgImageUrls,
): string | undefined {
  if (ogImage === undefined) {
    return undefined;
  }
  const url = ogImageUrls[ogImage];
  if (url === undefined) {
    throw new ArticleParseError(
      slug,
      `ogImage「${ogImage}」が記事用OGP画像のディレクトリにありません`,
    );
  }
  return url;
}

function validateSourceReferences(
  slug: string,
  body: string,
  sourcesCount: number,
): void {
  for (const number of extractSourceReferenceNumbers(body)) {
    if (number < 1 || number > sourcesCount) {
      throw new ArticleParseError(
        slug,
        `出典参照 [^${number}] に対応する sources がありません（sources は ${sourcesCount} 件）`,
      );
    }
  }
}

function extractFrontmatterSource(
  source: string,
): { frontmatter: string; body: string } | null {
  const lines = source.split(/\r?\n/);
  if (lines[0] !== FRONTMATTER_DELIMITER) {
    return null;
  }

  const endIndex = lines.findIndex(
    (line, index) => index > 0 && line === FRONTMATTER_DELIMITER,
  );
  if (endIndex === -1) {
    return null;
  }

  return {
    frontmatter: lines.slice(1, endIndex).join("\n"),
    body: lines.slice(endIndex + 1).join("\n"),
  };
}

/**
 * 記事が公開日を迎えているかどうか。publishedAtが未来の記事は、その日以降のビルドで公開される
 */
export type ArticlePublicationStatus = "draft" | "scheduled" | "public";

export function articlePublicationStatus(
  article: Pick<ArticleSummary, "published" | "publishedAt">,
  today: string,
): ArticlePublicationStatus {
  if (!article.published) {
    return "draft";
  }
  return article.publishedAt <= today ? "public" : "scheduled";
}

export function filterVisibleArticles(
  articles: readonly Article[],
  visibility: { includeUnpublished: boolean; today: string },
): Article[] {
  if (visibility.includeUnpublished) {
    return [...articles];
  }
  return articles.filter(
    (article) =>
      articlePublicationStatus(article, visibility.today) === "public",
  );
}

export function sortArticlesByPublishedAtDesc(
  articles: readonly Article[],
): Article[] {
  return [...articles].sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt),
  );
}

export function toArticleSummary(article: Article): ArticleSummary {
  return {
    slug: article.slug,
    title: article.title,
    description: article.description,
    publishedAt: article.publishedAt,
    updatedAt: article.updatedAt,
    category: article.category,
    tags: article.tags,
    published: article.published,
    ogImageUrl: article.ogImageUrl,
  };
}

export function formatArticleDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-");
  return `${year}年${Number(month)}月${Number(day)}日`;
}
